import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Menu } from "lucide-react";
import { API } from "../../constants";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('theme1');
  const [isThemeTransitioning, setIsThemeTransitioning] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Fetch settings for menu, logo and theme
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
        setCurrentTheme(res.data.active_theme || 'theme1');
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);
  
  // Listen for theme changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      const theme = sessionStorage.getItem('active_theme');
      if (theme && theme !== currentTheme) {
        setCurrentTheme(theme);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for same-tab changes
    const interval = setInterval(() => {
      const theme = sessionStorage.getItem('active_theme');
      if (theme && theme !== currentTheme) {
        setCurrentTheme(theme);
      }
    }, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentTheme]);
  
  // Default menu items if settings not loaded
  const defaultMenuItems = [
    { name: "Home", link: "/", enabled: true },
    { name: "News & Blog", link: "/news", enabled: true },
    { name: "Contact", link: "/contact", enabled: true },
  ];
  
  const menuItems = settings?.menu_items?.filter(item => item.enabled) || defaultMenuItems;
  const logoUrl = settings?.logo_url || "/logo.png";
  
  const isActive = (path) => location.pathname === path;
  
  // Check if link is external
  const isExternalLink = (link) => link?.startsWith('http://') || link?.startsWith('https://');
  
  const handleMenuClick = (link) => {
    if (isExternalLink(link)) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
    setMobileMenuOpen(false);
  };
  
  // Handle theme toggle
  const handleThemeToggle = async () => {
    const newTheme = currentTheme === 'theme1' ? 'theme2' : 'theme1';
    try {
      setIsThemeTransitioning(true);
      await axios.put(`${API}/settings`, { active_theme: newTheme });
      
      // Store in sessionStorage for cross-component sync
      sessionStorage.setItem('active_theme', newTheme);
      
      // Dispatch a custom event for same-tab communication
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: newTheme }));
      
      setCurrentTheme(newTheme);
      
      // Reload the page to apply theme change
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error("Error updating theme:", error);
    } finally {
      setIsThemeTransitioning(false);
    }
  };
  
  // Don't show header on admin page or login page
  if (location.pathname.startsWith("/cst")) {
    return null;
  }
  
  // Theme Toggle Button Component
  const ThemeToggleButton = () => (
    <button
      onClick={handleThemeToggle}
      disabled={isThemeTransitioning}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all shadow-md hover:shadow-lg hover:scale-105 text-xs bg-white border border-gray-200 disabled:opacity-50"
      title="Switch Theme"
    >
      <span className="font-medium text-gray-600">
        {currentTheme === 'theme1' ? '✨' : '🎨'}
      </span>
      <div className="w-7 h-3.5 bg-gray-200 rounded-full relative">
        <div 
          className={`absolute top-0.5 w-2.5 h-2.5 rounded-full transition-all duration-300 ${
            currentTheme === 'theme2' 
              ? 'right-0.5 bg-gradient-to-r from-orange-400 to-red-500' 
              : 'left-0.5 bg-gray-400'
          }`}
        ></div>
      </div>
    </button>
  );

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate("/")}
            >
              <img 
                src={logoUrl} 
                alt="CSBpathway" 
                className="h-10 md:h-14 w-auto object-contain"
              />
            </div>
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-6">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleMenuClick(item.link)}
                  className="relative group px-1 py-2 text-sm font-medium transition-all duration-200"
                >
                  <span className={`${isActive(item.link) ? 'text-red-600' : 'text-gray-700 group-hover:text-red-600'}`}>
                    {item.name}
                  </span>
                  {/* Gradient Underline */}
                  <span 
                    className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-red-600 via-red-500 to-red-400 transition-all duration-300 ${
                      isActive(item.link) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </button>
              ))}
              {/* Theme Toggle in Desktop Menu */}
              {location.pathname === "/" && <ThemeToggleButton />}
            </nav>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg animate-in slide-in-from-top duration-200">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleMenuClick(item.link)}
                  className={`relative flex items-center w-full px-4 py-3 rounded-xl text-left font-medium transition-all duration-200 ${
                    isActive(item.link)
                      ? "text-red-600 bg-red-50"
                      : "text-gray-700 hover:bg-gray-50 hover:text-red-600"
                  }`}
                >
                  {item.name}
                  {/* Side indicator for active */}
                  {isActive(item.link) && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-600 to-red-400 rounded-r-full" />
                  )}
                </button>
              ))}
              {/* Theme Toggle in Mobile Menu */}
              {location.pathname === "/" && (
                <div className="px-4 py-3 border-t border-gray-100 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Switch Theme</span>
                    <ThemeToggleButton />
                  </div>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
